import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import ORDER_PACKING_STATUS from '../../components/constants/orderPackingStatus';
import { useEditOrder } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import PackerDeliveryOrderItems from './packerDeliveryOrderItems';
import PackerRefunds from './packerRefunds';
import PackerCounter from './packerCounter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { connect } from 'react-redux';

const PackerDeliveryDayMarketplaceOrderRow = ({
  deliveryDayMarketplace,
  order,
  packedItemIds,
  addPackedItem,
  removePackedItemFromIndex,
  currentUser,
}) => {
  const [refundOrderItems, setRefundOrderItems] = useState([]);
  const [bagCount, setBagCount] = useState(order.bagCount);
  const [hideOrders, setHideOrders] = useState(order.packingStatus === ORDER_PACKING_STATUS.packed);
  const [packedLocation, setPackedLocation] = useState(order.packedLocation || '');

  const {
    editOrder,
    isLoading: editLoading,
    isError: isEditError,
    error: editError,
  } = useEditOrder({ deliveryDayMarketplaceId: deliveryDayMarketplace.id });

  const orderItems = order.items.filter((oi) => oi.fulfilledQuantity === null || oi.fulfilledQuantity > 0);
  const canMarkPacked = orderItems.every((orderItem) =>
    packedItemIds.some(({ itemId, orderId }) => itemId === orderItem.item.id && orderId === order.id)
  );

  const addRefundOrderItems = (itemsToAdd) => {
    const newRefundOrderItems = [...refundOrderItems].concat(itemsToAdd);
    setRefundOrderItems(newRefundOrderItems);
  };

  const removeRefundOrderItemFromIndex = (index) => {
    const newRefundOrderItems = [...refundOrderItems];
    newRefundOrderItems.splice(index, 1);
    setRefundOrderItems(newRefundOrderItems);
  };

  const updateRefundOrderItem = (newRoi, index) => {
    const newRefundOrderItems = [...refundOrderItems];

    // currently you can only change the payer on the orderId level, not per each item
    if (newRoi.payer !== newRefundOrderItems[index].payer) {
      newRefundOrderItems.forEach((roi) => (roi.orderId === newRoi.orderId ? (roi.payer = newRoi.payer) : null));
    }

    newRefundOrderItems[index] = newRoi;
    setRefundOrderItems(newRefundOrderItems);
  };

  const { formatMessage } = useIntl();

  const cancelPackingButton = (
    <button
      className="btn btn-default btn-small m-b-2 m-r-2"
      onClick={() => {
        if (
          window.confirm(
            formatMessage({
              id: 'packer.delivery.day.marketplace.inventory.individual.table.cta.mark.not.packed.confirm',
            })
          )
        ) {
          editOrder({
            order: {
              ...order,
              packingStatus: null,
            },
          });
        }
      }}
    >
      <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.table.cta.mark.not.packed'} />
    </button>
  );

  const inProgressButton = (
    <button
      className="btn btn-default btn-small m-b-2 m-r-2"
      onClick={() =>
        editOrder({
          order: {
            ...order,
            packingStatus: ORDER_PACKING_STATUS.inProgress,
          },
        })
      }
    >
      <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.table.cta.mark.in.progress'} />
    </button>
  );

  const markPrepackButton = (
    <button
      className="btn btn-default btn-small m-b-2 m-r-2"
      onClick={() =>
        editOrder({
          order: {
            ...order,
            packingStatus: ORDER_PACKING_STATUS.needsPrePacking,
          },
        })
      }
    >
      <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.table.cta.mark.needs.pre.packing'} />
    </button>
  );

  const packedButton = (
    <button
      className="btn btn-default btn-small m-b-2 m-r-2"
      disabled={!canMarkPacked}
      onClick={() =>
        editOrder({
          order: {
            ...order,
            packingStatus: ORDER_PACKING_STATUS.packed,
            bagCount,
            packedLocation,
          },
        })
      }
    >
      <FormattedMessage
        id={
          order.packingStatus === ORDER_PACKING_STATUS.packed
            ? 'packer.delivery.day.marketplace.inventory.individual.table.cta.edit.packed'
            : 'packer.delivery.day.marketplace.inventory.individual.table.cta.mark.packed'
        }
      />
    </button>
  );

  return (
    <div className="row striped-child p-t-3 p-b-3">
      <div className="col-12 m-b-2">
        <div>
          #{order.batchId} {order.businessProfile ? order.businessProfile.companyName : order.user.screenName}
        </div>
        <div className="text-muted">
          <small>ID: {order.id}</small>
        </div>
        <div className="text-muted m-b-2">
          <small>
            {utils.getFormattedTimeRange(order.timeRange)} {order.timeRange.serviceAreaName}
          </small>
        </div>
        <div>
          {order.packingStatus === ORDER_PACKING_STATUS.packed ? (
            `✅ ${order.packingStatus}`
          ) : order.packingStatus === ORDER_PACKING_STATUS.inProgress ? (
            `💃 ${order.packingStatus}`
          ) : order.packingStatus === ORDER_PACKING_STATUS.needsPrePacking ? (
            `⏰ ${order.packingStatus}`
          ) : (
            <small className="text-muted">
              <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.table.not.packed'} />
            </small>
          )}
        </div>
        {order.packer ? (
          <div className="m-b-2">
            <small>Packer: {order.packer.screenName}</small>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="col-12">
        <button className="btn btn-normal-link m-b-2" onClick={() => setHideOrders(!hideOrders)}>
          <FormattedMessage
            id={
              hideOrders
                ? 'packer.delivery.day.marketplace.inventory.individual.show.items'
                : 'packer.delivery.day.marketplace.inventory.individual.hide.items'
            }
          />{' '}
          {hideOrders ? <FontAwesomeIcon icon={faCaretUp} /> : <FontAwesomeIcon icon={faCaretDown} />}
        </button>
        {hideOrders ? (
          ''
        ) : (
          <PackerDeliveryOrderItems
            orderItems={orderItems}
            order={order}
            addToRefunds={addRefundOrderItems}
            refundOrderItems={refundOrderItems}
            removeFromRefunds={removeRefundOrderItemFromIndex}
            packedItemIds={packedItemIds}
            addPackedItem={addPackedItem}
            removePackedItemFromIndex={removePackedItemFromIndex}
          />
        )}
      </div>
      <div className="col-12">
        {refundOrderItems.length || order.payment.refunds.length ? (
          <>
            <hr />

            <PackerRefunds
              refundOrderItems={refundOrderItems}
              setRefundOrderItems={setRefundOrderItems}
              removeFromRefunds={removeRefundOrderItemFromIndex}
              updateRefundOrderItem={updateRefundOrderItem}
              orderBatchId={order.batchId}
              user={order.user}
              refunds={order.payment.refunds}
              invalidateQueryKey={'orders'}
            />
          </>
        ) : (
          ''
        )}

        <Loader show={editLoading} />
        {isEditError ? <ErrorMessage error={utils.getErrorMessageFromResponse(editError)} /> : ''}
      </div>
      {[ORDER_PACKING_STATUS.inProgress, ORDER_PACKING_STATUS.packed].includes(order.packingStatus) ? (
        <div className="col-12 col-md-6 m-b-3">
          <div className="m-b-2">
            <PackerCounter
              count={bagCount}
              onDecrease={() => setBagCount(bagCount - 1)}
              onIncrease={() => setBagCount(bagCount + 1)}
              onReset={() => setBagCount(0)}
            />
          </div>
          <div className="form-group field field-string">
            <label className="control-label">
              <FormattedMessage id={'packer.delivery.day.marketplace.inventory.individual.packed.location'} />
            </label>
            <input
              className="form-control"
              placeholder=""
              type="text"
              value={packedLocation}
              onChange={(e) => setPackedLocation(e.target.value)}
            />
          </div>
        </div>
      ) : (
        ''
      )}
      <div className="col-12 m-b-2 text-right text-sm-left">
        {order.packer && order.packer.id !== currentUser.id ? (
          <div className="alert alert-warning">
            <FormattedMessage
              id={'packer.delivery.day.marketplace.inventory.individual.order.being.packed.by.someone.else'}
            />
          </div>
        ) : (
          ''
        )}
        <div>
          {order.packingStatus === ORDER_PACKING_STATUS.packed ? (
            <>
              {packedButton}
              {cancelPackingButton}
            </>
          ) : order.packingStatus === ORDER_PACKING_STATUS.inProgress ? (
            <>
              {packedButton}
              {cancelPackingButton}
            </>
          ) : order.packingStatus === ORDER_PACKING_STATUS.needsPrePacking ? (
            <>{inProgressButton}</>
          ) : (
            markPrepackButton
          )}
        </div>
      </div>
    </div>
  );
};

PackerDeliveryDayMarketplaceOrderRow.propTypes = {
  order: PropTypes.object.isRequired,
  deliveryDayMarketplace: PropTypes.object.isRequired,
};

PackerDeliveryDayMarketplaceOrderRow.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

export default connect(mapStateToProps)(PackerDeliveryDayMarketplaceOrderRow);
